import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  billingPeriod: string;
  isFree: boolean;
  isDefault: boolean;
  isActive: boolean;
  maxUsers: number;
  maxRoles: number;
  maxLocations: number;
  maxProductListings: number;
  maxNewProductListings: number;
  maxUsedProductListings: number;
  trialDays: number;
  allowCustomRoles: boolean;
  permissionIds: string[];
}

export type SaveSubscriptionPlan = Omit<SubscriptionPlan, 'id'>;

export interface TenantSubscription {
  id: string;
  status: string;
  startsAt: string;
  endsAt?: string | null;
  plan: Pick<SubscriptionPlan,
    'id' | 'name' | 'price' | 'billingPeriod' | 'maxUsers' | 'maxRoles' | 'maxLocations' |
    'maxProductListings' | 'maxNewProductListings' | 'maxUsedProductListings'>;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionPlansService {
  private readonly api = inject(ApiService);
  list(includeInactive = false): Observable<SubscriptionPlan[]> {
    return this.api
      .get<ApiResponse<{ plans: SubscriptionPlan[] }> | { plans: SubscriptionPlan[] }>(
        API_ENDPOINTS.subscriptionPlans.base,
        { includeInactive },
      )
      .pipe(map((response) => unwrapApiResponse(response).plans ?? []));
  }
  save(plan: SaveSubscriptionPlan, id?: string): Observable<unknown> {
    return id
      ? this.api.put(`${API_ENDPOINTS.subscriptionPlans.base}/${id}`, plan)
      : this.api.post(API_ENDPOINTS.subscriptionPlans.base, plan);
  }
  assign(tenantId: string, planId: string): Observable<unknown> {
    return this.api.put(API_ENDPOINTS.subscriptionPlans.byTenant(tenantId), {
      planId,
      endsAt: null,
    });
  }
  forTenant(tenantId: string): Observable<TenantSubscription | null> {
    return this.api
      .get<ApiResponse<{ subscription: TenantSubscription | null }> | { subscription: TenantSubscription | null }>(
        API_ENDPOINTS.subscriptionPlans.byTenant(tenantId),
      )
      .pipe(map((response) => unwrapApiResponse(response).subscription ?? null));
  }
  mine(): Observable<any | null> {
    return this.api.get<ApiResponse<{ subscription: any | null }> | { subscription: any | null }>(
      API_ENDPOINTS.subscriptionPlans.me,
    ).pipe(map((response) => unwrapApiResponse(response).subscription ?? null));
  }
  selectMine(planId: string): Observable<{ status: string; paymentRequired: boolean }> {
    return this.api.post<ApiResponse<{ status: string; paymentRequired: boolean }> | { status: string; paymentRequired: boolean }, { planId: string }>(
      API_ENDPOINTS.subscriptionPlans.selectMine, { planId })
      .pipe(map((response) => unwrapApiResponse(response)));
  }
}
